import type { AddLine } from './cart-store';
import { displaySellerName } from './hooks';
import type { ResolvedBrand } from '@mlm/shared';
import type { DisplayProduct } from './types';

/**
 * Building storefront cart lines outside the storefront.
 *
 * The storefront cart snapshots price, PV, cashback, stock, seller and
 * virtual-ness onto each line — unlike the retired portal cart, which was a bare
 * `Record<productId, qty>`. Every surface that now feeds this cart (the
 * dashboard's activation-kit grid, Buy Again on the orders page) has to build
 * the same shape, so it is built ONCE here rather than three times slightly
 * differently.
 *
 * Both functions take a `DisplayProduct` — the normalized row `toDisplay()`
 * produces from either catalog path — so variation pricing, image URLs and
 * per-currency prices are already resolved and can't be re-derived wrongly.
 */

export type AddLineOpts = {
  qty?: number;
  /** Which variation, when the product has them. */
  variant?: DisplayProduct['variants'][number];
};

/**
 * One catalog row → one cart line.
 *
 * A chosen variation overrides price, PV-bearing stock and per-currency prices:
 * variation prices deliberately do NOT inherit from the product, so reading the
 * parent's would quote a price checkout won't honour.
 */
export function productToAddLine(
  p: DisplayProduct,
  brand: ResolvedBrand,
  opts: AddLineOpts = {},
): AddLine {
  const v = opts.variant;
  return {
    productId: p.id,
    name: p.name,
    price: v ? v.price : p.price,
    was: v ? v.was || v.price : p.was || p.price,
    priceGc: v ? v.priceGc : p.priceGc,
    priceCoin: v ? v.priceCoin : p.priceCoin,
    image: p.image,
    variation: v ? v.name : '',
    variantId: v?.id,
    pv: p.pv,
    cashback: p.cashback ?? 0,
    isVirtual: p.isVirtual,
    // `stockQty` is null for an untracked (virtual) product; the cart reads
    // `undefined` as "unknown, don't cap", which is the same thing here.
    stock: v ? v.stockQty : (p.stockQty ?? undefined),
    sellerId: p.sellerId,
    sellerName: displaySellerName(p.sellerId, p.sellerName, brand),
    qty: opts.qty ?? 1,
  };
}

/** A past order's line, as the orders list carries it. */
export type ReorderItem = {
  productId: string;
  variantId?: string | null;
  qty: number;
};

/**
 * Past order lines → cart lines, re-priced from the LIVE catalog.
 *
 * Variant-aware on purpose: order items carry `variantId`, and re-adding by
 * product id alone drops the variation — the server then prices and
 * stock-checks the PARENT, reading the wrong inventory bucket. That is the
 * defect that broke variation checkout once already, and the old Buy Again had
 * exactly that shape.
 *
 * A line whose product or variation has since left the catalog is skipped and
 * counted, so the caller can say so instead of silently handing the buyer a
 * shorter cart than they asked for.
 */
export function orderItemsToAddLines(
  items: ReorderItem[],
  catalog: DisplayProduct[],
  brand: ResolvedBrand,
): { lines: AddLine[]; missingCount: number } {
  const byId = new Map(catalog.map((p) => [p.id, p]));
  const lines: AddLine[] = [];
  let missingCount = 0;

  for (const it of items) {
    const p = byId.get(it.productId);
    if (!p) {
      missingCount++;
      continue;
    }
    if (it.variantId) {
      const variant = p.variants.find((x) => x.id === it.variantId);
      if (!variant) {
        missingCount++;
        continue;
      }
      lines.push(productToAddLine(p, brand, { qty: it.qty, variant }));
      continue;
    }
    // A product that has GAINED variations since the order can't be re-added
    // blind — the server refuses a line naming no variant (`variant_required`).
    if (p.variants.length > 0) {
      missingCount++;
      continue;
    }
    lines.push(productToAddLine(p, brand, { qty: it.qty }));
  }

  return { lines, missingCount };
}
